import React, { useState, useEffect } from 'react';
import ColumnSelector from './ColumnSelector';
import PlayerHeader from './PlayerHeader';
import PlayerSearch from './PlayerSearch';
import './PlayerGameLogs.css';

// Get the API base URL (same as api.js)
const getApiBaseUrl = () => {
    if (process.env.REACT_APP_API_BASE_URL) {
        return process.env.REACT_APP_API_BASE_URL;
    }
    if (process.env.NODE_ENV === 'development') {
        return 'http://localhost:8000';
    }
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
};

const API_BASE_URL = getApiBaseUrl();

const PlayerGameLogs = ({ player, onPlayerSelect }) => {
    const [gameLogs, setGameLogs] = useState([]);
    const [seasonSummary, setSeasonSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSeason, setSelectedSeason] = useState('2025-26');
    const [selectedSeasonType, setSelectedSeasonType] = useState('Regular Season + Playoffs');
    const [sortField, setSortField] = useState('game_date');
    const [sortDirection, setSortDirection] = useState('desc');
    const [filterText, setFilterText] = useState('');
    const [showColumnSelector, setShowColumnSelector] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState([
        'game_date', 'matchup', 'min', 'pts', 'reb', 'ast', 'stl', 'blk', 'tov', 'fg_pct', 'plus_minus'
    ]);

    const seasons = [
        '2025-26', '2024-25', '2023-24', '2022-23', '2021-22', '2020-21',
        '2019-20', '2018-19', '2017-18', '2016-17', '2015-16',
        '2014-15', '2013-14', '2012-13', '2011-12', '2010-11',
        '2009-10', '2008-09', '2007-08', '2006-07', '2005-06',
        '2004-05', '2003-04', '2002-03', '2001-02', '2000-01',
        '1999-00', '1998-99', '1997-98', '1996-97', '1995-96',
        '1994-95', '1993-94', '1992-93', '1991-92', '1990-91',
        '1989-90', '1988-89', '1987-88', '1986-87', '1985-86',
        '1984-85', '1983-84', '1982-83', '1981-82', '1980-81',
        '1979-80', '1978-79', '1977-78', '1976-77', '1975-76',
        '1974-75', '1973-74', '1972-73', '1971-72', '1970-71',
        '1969-70', '1968-69', '1967-68', '1966-67', '1965-66',
        '1964-65', '1963-64', '1962-63', '1961-62', '1960-61',
        '1959-60', '1958-59', '1957-58', '1956-57', '1955-56',
        '1954-55', '1953-54', '1952-53', '1951-52', '1950-51',
        '1949-50', '1948-49', '1947-48', '1946-47'
    ];

    const seasonTypes = ['Regular Season + Playoffs', 'Playoffs', 'Pre Season'];

    // Column definitions
    const columns = [
        { key: 'game_date', label: 'Date' },
        { key: 'matchup', label: 'Matchup' },
        { key: 'wl', label: 'Result' },
        { key: 'min', label: 'MIN' },
        { key: 'pts', label: 'PTS' },
        { key: 'reb', label: 'REB' },
        { key: 'ast', label: 'AST' },
        { key: 'stl', label: 'STL' },
        { key: 'blk', label: 'BLK' },
        { key: 'tov', label: 'TOV' },
        { key: 'fg_pct', label: 'FG%' },
        { key: 'plus_minus', label: '+/-' }
    ];

    useEffect(() => {
        if (player) {
            fetchGameLogs();
            fetchSeasonSummary();
        }
    }, [player, selectedSeason, selectedSeasonType]);

    const fetchGameLogs = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(
                `${API_BASE_URL}/api/v1/dev/player-logs/player/${player.id}/game-logs?` +
                `season=${encodeURIComponent(selectedSeason)}&` +
                `season_type=${encodeURIComponent(selectedSeasonType)}`
            );

            if (!response.ok) {
                // Check if we're getting HTML instead of JSON (common in production)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    throw new Error('API server not available. Please check if the backend is running.');
                }
                throw new Error(`Failed to fetch game logs: ${response.statusText}`);
            }

            const data = await response.json();
            setGameLogs(data.game_logs || []);
        } catch (err) {
            // Handle JSON parsing errors specifically
            if (err.name === 'SyntaxError' && err.message.includes('Unexpected token')) {
                setError('API server not available. Please check if the backend is running.');
            } else {
                setError(err.message);
            }
            console.error('Error fetching game logs:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchSeasonSummary = async () => {
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/v1/dev/player-logs/player/${player.id}/season-summary?` +
                `season=${encodeURIComponent(selectedSeason)}`
            );

            if (response.ok) {
                const data = await response.json();
                setSeasonSummary(data.summary);
            } else {
                // Check if we're getting HTML instead of JSON (common in production)
                const contentType = response.headers.get('content-type');
                if (contentType && contentType.includes('text/html')) {
                    console.error('API server not available for season summary');
                }
            }
        } catch (err) {
            // Handle JSON parsing errors specifically
            if (err.name === 'SyntaxError' && err.message.includes('Unexpected token')) {
                console.error('API server not available for season summary');
            } else {
                console.error('Error fetching season summary:', err);
            }
        }
    };

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('desc');
        }
    };

    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(col => col !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const sortedAndFilteredLogs = gameLogs
        .filter(log => {
            if (!filterText) return true;
            const searchText = filterText.toLowerCase();
            return (
                log.matchup.toLowerCase().includes(searchText) ||
                log.wl.toLowerCase().includes(searchText) ||
                log.game_date.includes(searchText)
            );
        })
        .sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];

            // Handle numeric sorting
            if (['pts', 'reb', 'ast', 'stl', 'blk', 'tov', 'fgm', 'fga', 'fg3m', 'fg3a', 'ftm', 'fta', 'plus_minus'].includes(sortField)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }

            // Handle percentage sorting
            if (['fg_pct', 'fg3_pct', 'ft_pct'].includes(sortField)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }

            if (sortDirection === 'asc') {
                return aVal > bVal ? 1 : -1;
            } else {
                return aVal < bVal ? 1 : -1;
            }
        });

    const formatPercentage = (value) => {
        if (value === null || value === undefined) return '-';
        return `${(parseFloat(value) * 100).toFixed(1)}%`;
    };

    const formatMinutes = (minutes) => {
        if (!minutes) return '-';
        return minutes;
    };

    if (loading) {
        return (
            <div className="game-logs-container">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading game logs...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="game-logs-container">
                <div className="error-message">
                    <h3>Error Loading Game Logs</h3>
                    <p>{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="game-logs-container">
            {/* Player Search Bar */}
            <div className="player-search-section">
                <PlayerSearch onPlayerSelect={onPlayerSelect} />
            </div>
            
            {/* Enhanced Player Header */}
            <PlayerHeader player={player} />
            
            {/* Season Controls */}
            <div className="season-controls">
                <div className="control-group">
                    <label htmlFor="season-select">Season:</label>
                    <select
                        id="season-select"
                        value={selectedSeason}
                        onChange={(e) => setSelectedSeason(e.target.value)}
                        className="season-select"
                    >
                        {seasons.map(season => (
                            <option key={season} value={season}>{season}</option>
                        ))}
                    </select>
                </div>

                <div className="control-group">
                    <label htmlFor="season-type-select">Type:</label>
                    <select
                        id="season-type-select"
                        value={selectedSeasonType}
                        onChange={(e) => setSelectedSeasonType(e.target.value)}
                        className="season-type-select"
                    >
                        {seasonTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                        ))}
                    </select>
                </div>

                <div className="control-group">
                    <label htmlFor="filter-input">Filter:</label>
                    <input
                        id="filter-input"
                        type="text"
                        value={filterText}
                        onChange={(e) => setFilterText(e.target.value)}
                        placeholder="Filter by opponent, result, or date..."
                        className="filter-input"
                    />
                </div>
                <div className="control-group">
                    <button
                        type="button"
                        onClick={() => setShowColumnSelector(!showColumnSelector)}
                        className="column-selector-toggle"
                    >
                        {showColumnSelector ? 'Hide Columns' : 'Show Columns'}
                    </button>
                </div>
            </div>

            {/* Season Summary */}
            {seasonSummary && (
                <div className="season-summary">
                    <h3>Season Summary ({selectedSeason})</h3>
                    <div className="summary-stats">
                        <div className="summary-stat">
                            <span className="stat-label">Games:</span>
                            <span className="stat-value">{seasonSummary.games_played}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">PPG:</span>
                            <span className="stat-value">{seasonSummary.avg_pts?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">RPG:</span>
                            <span className="stat-value">{seasonSummary.avg_reb?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">APG:</span>
                            <span className="stat-value">{seasonSummary.avg_ast?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">SPG:</span>
                            <span className="stat-value">{seasonSummary.avg_stl?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">BPG:</span>
                            <span className="stat-value">{seasonSummary.avg_blk?.toFixed(1) || '-'}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">FG%:</span>
                            <span className="stat-value">{formatPercentage(seasonSummary.fg_pct)}</span>
                        </div>
                        <div className="summary-stat">
                            <span className="stat-label">3P%:</span>
                            <span className="stat-value">{formatPercentage(seasonSummary.fg3_pct)}</span>
                        </div>
                    </div>
                </div>
            )}



            {/* Column Selector */}
            {showColumnSelector && (
                <ColumnSelector
                    columns={columns}
                    visibleColumns={visibleColumns}
                    onColumnToggle={handleColumnToggle}
                />
            )}

            {/* Game Logs Table */}
            <div className="game-logs-table-container">
                <h3>Game Logs ({sortedAndFilteredLogs.length} games)</h3>
                
                {sortedAndFilteredLogs.length === 0 ? (
                    <div className="no-games">
                        <p>No games found for {selectedSeason} {selectedSeasonType}</p>
                    </div>
                ) : (
                    <div className="table-wrapper">
                        <table className="game-logs-table">
                            <thead>
                                <tr>
                                    {visibleColumns.map(columnKey => {
                                        const column = columns.find(col => col.key === columnKey);
                                        if (!column) return null;
                                        
                                        return (
                                            <th key={columnKey} onClick={() => handleSort(columnKey)} className="sortable">
                                                {column.label} {sortField === columnKey && (sortDirection === 'asc' ? '↑' : '↓')}
                                            </th>
                                        );
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {sortedAndFilteredLogs.map((game, index) => (
                                    <tr key={`${game.game_id}-${index}`} className={game.wl === 'W' ? 'win' : 'loss'}>
                                        {visibleColumns.map(columnKey => {
                                            const column = columns.find(col => col.key === columnKey);
                                            if (!column) return null;
                                            
                                            let cellContent = '';
                                            let cellClassName = '';
                                            
                                            switch (columnKey) {
                                                case 'game_date':
                                                    cellContent = game.game_date;
                                                    break;
                                                case 'matchup':
                                                    cellContent = game.matchup;
                                                    break;
                                                case 'wl':
                                                    cellContent = game.wl;
                                                    cellClassName = game.wl === 'W' ? 'win' : 'loss';
                                                    break;
                                                case 'min':
                                                    cellContent = formatMinutes(game.min);
                                                    break;
                                                case 'pts':
                                                    cellContent = game.pts;
                                                    break;
                                                case 'reb':
                                                    cellContent = game.reb;
                                                    break;
                                                case 'ast':
                                                    cellContent = game.ast;
                                                    break;
                                                case 'stl':
                                                    cellContent = game.stl;
                                                    break;
                                                case 'blk':
                                                    cellContent = game.blk;
                                                    break;
                                                case 'tov':
                                                    cellContent = game.tov;
                                                    break;
                                                case 'fg_pct':
                                                    cellContent = formatPercentage(game.fg_pct);
                                                    break;
                                                case 'plus_minus':
                                                    cellContent = game.plus_minus;
                                                    cellClassName = parseInt(game.plus_minus) >= 0 ? 'positive' : 'negative';
                                                    break;
                                                default:
                                                    cellContent = game[columnKey] || '';
                                            }
                                            
                                            return (
                                                <td key={columnKey} className={cellClassName}>
                                                    {cellContent}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlayerGameLogs; 